import { useMemo, useState } from 'react';
import type { DatabaseCodeChanges, DatabaseCodeFile, DatabaseCodeKind } from '../api';
import { plural } from '../labels';
import { FilterChips } from './primitives';

/**
 * DatabaseCodeView: the exact code of a Magento release's database
 * changes. Every database-relevant file that differs from the live release
 * (declarative schema, whitelists, module declarations, Setup/ patches and
 * scripts), in the project's own modules and in vendor packages alike,
 * with its unified diff. Read from files on the server; nothing here ran
 * against the database.
 */

export const codeKindLabel: Record<DatabaseCodeKind, { one: string; many: string }> = {
  schema: { one: 'Declarative schema', many: 'declarative schemas' },
  schema_whitelist: { one: 'Schema whitelist', many: 'schema whitelists' },
  schema_patch: { one: 'Schema patch', many: 'schema patches' },
  data_patch: { one: 'Data patch', many: 'data patches' },
  setup_script: { one: 'Setup script', many: 'setup scripts' },
  module_declaration: { one: 'Module declaration', many: 'module declarations' },
  setup_other: { one: 'Other setup code', many: 'other setup files' },
};

const kindOrder: DatabaseCodeKind[] = ['schema', 'schema_patch', 'data_patch', 'setup_script', 'module_declaration', 'schema_whitelist', 'setup_other'];

const changeWord: Record<DatabaseCodeFile['change'], string> = {
  added: 'New file',
  removed: 'Removed',
  modified: 'Changed',
};

type OriginFilter = 'all' | 'app_code' | 'vendor';

/** Files listed before "Show more": a Magento upgrade changes hundreds. */
const pageSize = 60;

/** One sentence: how many files, of which kinds. */
export function codeSummary(code: DatabaseCodeChanges): string {
  const counts = kindOrder
    .map((kind) => ({ kind, n: code.summary?.[kind] ?? 0 }))
    .filter((c) => c.n > 0)
    .map((c) => (c.n === 1 ? `1 ${codeKindLabel[c.kind].one.toLowerCase()}` : `${c.n} ${codeKindLabel[c.kind].many}`));
  return counts.length > 0 ? counts.join(', ') : plural(code.file_count, 'file');
}

function moduleSentence(modules: NonNullable<DatabaseCodeChanges['modules']>): string | null {
  const parts: string[] = [];
  if (modules.added.length > 0) parts.push(`adds ${modules.added.join(', ')}`);
  if (modules.enabled.length > 0) parts.push(`enables ${modules.enabled.join(', ')}`);
  if (modules.disabled.length > 0) parts.push(`disables ${modules.disabled.join(', ')}`);
  if (modules.removed.length > 0) parts.push(`removes ${modules.removed.join(', ')}`);
  return parts.length > 0 ? `app/etc/config.php ${parts.join('; ')}.` : null;
}

export function DatabaseCodeView({ code, title = 'Code that changes the database' }: { code: DatabaseCodeChanges; title?: string }) {
  const [origin, setOrigin] = useState<OriginFilter>('all');
  const [shown, setShown] = useState(pageSize);
  const counts = useMemo(
    () => ({
      app_code: code.files.filter((f) => f.origin === 'app_code').length,
      vendor: code.files.filter((f) => f.origin === 'vendor').length,
    }),
    [code.files],
  );
  const files = origin === 'all' ? code.files : code.files.filter((f) => f.origin === origin);
  const modules = code.modules ? moduleSentence(code.modules) : null;

  let status: React.ReactNode = null;
  switch (code.state) {
    case 'pending':
      status = 'Not read yet · runs once the release is built';
      break;
    case 'not_run':
      status = 'Not read · the run ended before the code comparison';
      break;
    case 'not_available':
      status = code.reason ?? 'The server’s agent is older than the code comparison; update it to see the exact code.';
      break;
    case 'unreadable':
      status = `The code could not be compared: ${code.reason?.replace(/\.$/, '') ?? 'no reason was given'}.`;
      break;
    case 'no_live_release':
      status = code.file_count > 0 ? 'No live release · every database file of this release is new' : 'No live release · this release has no database files';
      break;
    default:
      status =
        code.file_count === 0
          ? 'No database-relevant file changes'
          : `${plural(code.file_count, 'file')} differ from the live release: ${codeSummary(code)}.`;
  }

  return (
    <section className="db-code" aria-labelledby="db-code-title" data-testid="database-code">
      <div className="db-code__head">
        <h3 id="db-code-title" className="schema-changes__title">
          {title}
        </h3>
        {code.files.length > 0 && counts.app_code > 0 && counts.vendor > 0 ? (
          <FilterChips
            label="Where the files are"
            value={origin}
            onChange={(next) => {
              setOrigin(next);
              setShown(pageSize);
            }}
            options={[
              { value: 'all', label: 'All', count: code.files.length },
              { value: 'app_code', label: 'Your code', count: counts.app_code },
              { value: 'vendor', label: 'Vendor packages', count: counts.vendor },
            ]}
          />
        ) : null}
      </div>
      <p className="panel__note" data-testid="database-code-summary">
        {status}
        {modules ? ` ${modules}` : null}
      </p>
      {code.source_release && code.target_release && code.state === 'compared' ? (
        <p className="type-meta db-code__releases">
          <span className="visually-hidden">Compared: </span>
          Live <code translate="no">{code.source_release}</code> → <code translate="no">{code.target_release}</code>
        </p>
      ) : null}

      {files.length > 0 ? (
        <ul className="db-code__files" aria-label="Changed files">
          {files.slice(0, shown).map((file) => (
            <CodeFileRow key={`${file.origin}:${file.path}`} file={file} />
          ))}
        </ul>
      ) : null}
      {files.length > shown ? (
        <button type="button" className="button button--secondary button--small db-code__more" onClick={() => setShown((n) => n + pageSize)}>
          Show {Math.min(pageSize, files.length - shown)} more of {files.length - shown}
        </button>
      ) : null}
      {code.files_truncated ? (
        <p className="panel__note muted">The list stops at {code.files.length} of {code.file_count} files; the rest were counted but not listed.</p>
      ) : null}
    </section>
  );
}

function CodeFileRow({ file }: { file: DatabaseCodeFile }) {
  // The diff renders only once opened: a vendor upgrade lists hundreds.
  const [open, setOpen] = useState(false);
  const slash = file.path.lastIndexOf('/');
  return (
    <li className="db-code__file">
      <details onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary className="db-code__summary">
          <span className="db-code__path" translate="no">
            <span className="db-code__dir">{file.path.slice(0, slash + 1)}</span>
            <span className="db-code__name">{file.path.slice(slash + 1)}</span>
          </span>
          <span className="db-code__meta">
            <span className={`db-code__change db-code__change--${file.change}`}>{changeWord[file.change]}</span>
            <span>{codeKindLabel[file.kind]?.one ?? file.kind}</span>
            {file.module ? (
              <span className="db-code__module" translate="no">
                {file.module}
              </span>
            ) : null}
          </span>
          <span className="db-code__counts" aria-label={`${file.lines_added} lines added, ${file.lines_removed} removed`}>
            <span className="db-code__added">+{file.lines_added}</span>
            <span className="db-code__removed">−{file.lines_removed}</span>
          </span>
        </summary>
        {open ? <FileDiff file={file} /> : null}
      </details>
    </li>
  );
}

type DiffLine = { kind: 'hunk' | 'add' | 'del' | 'ctx' | 'note'; text: string; oldNo?: number; newNo?: number };

/** parseUnifiedDiff reads a unified diff into numbered lines. */
export function parseUnifiedDiff(diff: string): DiffLine[] {
  const out: DiffLine[] = [];
  let oldNo = 0;
  let newNo = 0;
  for (const line of diff.replace(/\n$/, '').split('\n')) {
    if (line.startsWith('--- ') || line.startsWith('+++ ')) continue;
    const hunk = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@(.*)$/.exec(line);
    if (hunk) {
      oldNo = Number(hunk[1]);
      newNo = Number(hunk[2]);
      out.push({ kind: 'hunk', text: line });
    } else if (line.startsWith('+')) {
      out.push({ kind: 'add', text: line.slice(1), newNo: newNo++ });
    } else if (line.startsWith('-')) {
      out.push({ kind: 'del', text: line.slice(1), oldNo: oldNo++ });
    } else if (line.startsWith('\\')) {
      out.push({ kind: 'note', text: line.slice(1).trim() });
    } else {
      out.push({ kind: 'ctx', text: line.slice(1), oldNo: oldNo++, newNo: newNo++ });
    }
  }
  return out;
}

const signOf: Record<DiffLine['kind'], string> = { hunk: '', add: '+', del: '−', ctx: ' ', note: '' };

function FileDiff({ file }: { file: DatabaseCodeFile }) {
  if (file.diff_omitted) {
    return (
      <p className="db-code__note">
        The diff is not shown: this comparison reached its size limit before this file. It {file.change === 'modified' ? 'changes' : file.change === 'added' ? 'adds' : 'removes'}{' '}
        {plural(file.lines_added + file.lines_removed, 'line')}.
      </p>
    );
  }
  if (!file.diff) return <p className="db-code__note">No line changes to show.</p>;
  const lines = parseUnifiedDiff(file.diff);
  return (
    <>
      <div className="db-code__diff" role="region" aria-label={`Diff of ${file.path}`} tabIndex={0}>
        <table className="db-code__table">
          <tbody>
            {lines.map((l, i) =>
              l.kind === 'hunk' || l.kind === 'note' ? (
                <tr key={i} className={`db-code__line db-code__line--${l.kind}`}>
                  <td colSpan={4}>{l.text}</td>
                </tr>
              ) : (
                <tr key={i} className={`db-code__line db-code__line--${l.kind}`}>
                  <td className="db-code__no" aria-hidden="true">{l.oldNo ?? ''}</td>
                  <td className="db-code__no" aria-hidden="true">{l.newNo ?? ''}</td>
                  <td className="db-code__sign">
                    <span aria-hidden="true">{signOf[l.kind]}</span>
                    <span className="visually-hidden">{l.kind === 'add' ? 'Added' : l.kind === 'del' ? 'Removed' : ''}</span>
                  </td>
                  <td className="db-code__text" translate="no">{l.text}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {file.diff_truncated ? <p className="db-code__note">The diff is cut at 64 KiB; the rest of the file’s changes are not shown.</p> : null}
    </>
  );
}
